import { PRODUCT_STEPS } from '@/lib/products';
import {
  allFamiliesResolved,
  hasActionableCoverage,
} from '@/types/insurance-builder-eligibility-revamp';
import { getActionableInsurances } from '@/types/insurance-builder.type';

import { MILESTONES } from '../milestones';
import type { WizardVariantDefinition } from '../wizard';

export const eligibilityRevampVariant = {
  id: 'eligibility-revamp',
  name: 'Eligibility Revamp',
  startPath: '/0',
  milestones: [
    {
      id: MILESTONES.CATEGORY_SELECTED,
      label: 'Choose Your Supplies',
      description: 'Select the types of medical supplies you need',
      startPath: '/0',
      routes: ['/0'],
      isComplete: (state) => state.selectedCategoryIds.length > 0,
    },
    {
      id: MILESTONES.PATIENT_INFO_COLLECTED,
      label: 'Patient Information',
      description: 'Provide basic details about the patient',
      startPath: '/1',
      routes: ['/1'],
      isComplete: (state) =>
        Boolean(
          state.patientRecipient &&
          state.patientFirstName &&
          state.patientLastName &&
          state.patientDateOfBirth &&
          state.patientGender &&
          state.insuranceZipCode
        ),
    },
    {
      id: MILESTONES.INSURANCE_DETERMINED,
      label: 'Insurance Verification',
      description: 'Confirm your insurance coverage',
      startPath: '/1',
      routes: [
        '/insurance/discovery-pending',
        '/insurance/resolve',
        '/insurance/upload',
        '/insurance/verifying',
        '/insurance/benefits',
        '/insurance/send-link',
      ],
      isComplete: (state) => {
        const builder = state.insuranceBuilder;
        const actionable = getActionableInsurances(builder);
        return (
          allFamiliesResolved(builder) && (hasActionableCoverage(builder) || actionable.length > 0)
        );
      },
    },
    {
      id: MILESTONES.PROFILE_CREATED,
      label: 'Contact & Shipping',
      description: 'Add your contact info and delivery address',
      startPath: '/insurance/benefits',
      routes: [
        '/create-profile/for-me',
        '/create-profile/for-someone-else',
        '/create-profile/for-someone-else/relationship',
        '/create-profile/for-someone-else/shipping',
      ],
      isComplete: (state) =>
        Boolean(
          state.contactEmail &&
          state.contactPhoneNumber &&
          state.contactAddressLine1 &&
          state.contactCity &&
          state.contactState &&
          state.contactZip
        ),
    },
    {
      id: MILESTONES.PRODUCTS_SELECTED,
      label: 'Select Products',
      description: 'Choose your medical supplies',
      startPath: PRODUCT_STEPS[0]?.url ?? '/products/incontinence',
      routes: PRODUCT_STEPS.map((step) => step.url),
      isComplete: (state) => state.productSelectionConfirmed === true,
    },
    {
      id: MILESTONES.APPOINTMENT_RESERVED,
      label: 'Schedule Appointment',
      description: 'Book your telehealth consultation',
      startPath: '/appointment/intro',
      routes: ['/appointment/intro', '/appointment/book-call'],
      isComplete: (state) => !!state.schedulingSelectedSlot,
    },
    {
      id: MILESTONES.PHONE_VERIFIED,
      label: 'Verify Phone Number',
      description: 'Confirm your phone number for the appointment',
      startPath: '/verify-profile/phone-number',
      routes: ['/verify-profile/phone-number', '/verify-profile/phone-number/code'],
      isComplete: (state) => state.contactPhoneNumberVerified === true,
    },
  ],

  exitMilestones: [
    {
      id: MILESTONES.ORDER_COMPLETED,
      label: 'Order Complete',
      description: 'Your order has been submitted',
      route: '/appointment/confirmed',
    },
    {
      id: MILESTONES.WAITLISTED,
      label: 'Waitlist',
      description: 'You have been added to our waitlist',
      route: '/insurance/waitlist/thank-you',
    },
    {
      id: MILESTONES.REACH_OUT_PENDING,
      label: 'Insurance Link Sent',
      description: 'We sent a link so you can add insurance later',
      route: '/insurance/link-sent',
    },
  ],
} satisfies WizardVariantDefinition<'eligibility-revamp'>;
